import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api } from "@/lib/api";

export interface User {
  id: string;
  email: string;
  emailVerified: boolean;
  credits: number;
  role: string;
  mfaEnabledAt: string | null;
}

export type SignInError = "bad_credentials" | "email_not_verified" | "unknown";
export type SignUpError = "email_taken" | "weak_password" | "invalid_email" | "unknown";

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ ok: boolean; error?: SignInError }>;
  signUp: (email: string, password: string) => Promise<{ ok: boolean; error?: SignUpError }>;
  signOut: () => Promise<void>;
  spendCoins: (amount: number) => void;
}

// Wire shape returned by the backend (snake_case).
interface ApiUser {
  id: string;
  email: string;
  email_verified: boolean;
  role: string;
  mfa_enabled_at: string | null;
  wallet_balance: number;
}

function toClientUser(a: ApiUser): User {
  return {
    id: a.id,
    email: a.email,
    emailVerified: a.email_verified,
    credits: a.wallet_balance ?? 0,
    role: a.role ?? "user",
    mfaEnabledAt: a.mfa_enabled_at ?? null,
  };
}

const STORAGE_KEY = "automaton.session";

function readStoredUser(): User | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof (parsed as { id?: unknown }).id === "string" &&
      typeof (parsed as { email?: unknown }).email === "string" &&
      typeof (parsed as { emailVerified?: unknown }).emailVerified === "boolean" &&
      typeof (parsed as { credits?: unknown }).credits === "number"
    ) {
      const p = parsed as User;
      return {
        id: p.id,
        email: p.email,
        emailVerified: p.emailVerified,
        credits: p.credits,
        role: typeof p.role === "string" ? p.role : "user",
        mfaEnabledAt: typeof p.mfaEnabledAt === "string" ? p.mfaEnabledAt : null,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function writeStoredUser(user: User | null): void {
  if (typeof window === "undefined") return;
  if (user === null) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => readStoredUser());
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    // Resetting to true on (re-)setup keeps mountedRef correct under StrictMode's
    // double-invoke (setup → cleanup → setup) so the /auth/me response is not ignored.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Sync user to localStorage after auth initialises; avoids overwriting the hint during load.
  useEffect(() => {
    if (!loading) {
      writeStoredUser(user);
    }
  }, [user, loading]);

  useEffect(() => {
    async function init() {
      const result = await api.get<{ user: ApiUser | null }>("/v1/auth/me");
      if (!mountedRef.current) return;
      if (result.status === 0) {
        // Network error — keep localStorage hint as-is
        setLoading(false);
        return;
      }
      let serverUser: User | null = null;
      if (result.ok && result.body?.user) {
        serverUser = toClientUser(result.body.user);
      }
      setUser(serverUser);
      setLoading(false);
    }
    void init();
  }, []);

  const signIn = useCallback(
    async (email: string, password: string): Promise<{ ok: boolean; error?: SignInError }> => {
      const result = await api.post<{ user: ApiUser }>("/v1/auth/login", { email, password });
      if (!mountedRef.current) return { ok: false, error: "unknown" };
      if (result.ok && result.body?.user) {
        setUser(toClientUser(result.body.user));
        return { ok: true };
      }
      if (result.status === 401) {
        return { ok: false, error: "bad_credentials" };
      }
      if (
        result.status === 403 &&
        (result.body as { error?: string } | null)?.error === "email_not_verified"
      ) {
        return { ok: false, error: "email_not_verified" };
      }
      return { ok: false, error: "unknown" };
    },
    [],
  );

  const signUp = useCallback(
    async (email: string, password: string): Promise<{ ok: boolean; error?: SignUpError }> => {
      const result = await api.post<{ user: ApiUser }>("/v1/auth/register", { email, password });
      if (!mountedRef.current) return { ok: false, error: "unknown" };
      if (result.status === 201 && result.body?.user) {
        setUser(toClientUser(result.body.user));
        return { ok: true };
      }
      const errBody = result.body as { error?: string } | null;
      if (result.status === 400 && errBody?.error === "weak_password") {
        return { ok: false, error: "weak_password" };
      }
      if (result.status === 400 && errBody?.error === "invalid_email") {
        return { ok: false, error: "invalid_email" };
      }
      if (result.status === 409) {
        return { ok: false, error: "email_taken" };
      }
      return { ok: false, error: "unknown" };
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await api.post("/v1/auth/logout", {});
    if (!mountedRef.current) return;
    setUser(null);
  }, []);

  const spendCoins = useCallback((amount: number) => {
    setUser((prev: User | null) =>
      prev ? { ...prev, credits: Math.max(0, prev.credits - amount) } : prev,
    );
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, signIn, signUp, signOut, spendCoins }),
    [user, loading, signIn, signUp, signOut, spendCoins],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook + provider co-located by design; splitting yields no HMR benefit here.
export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
